// Shared date-range presets for the pipeline filters.
// Returns {from, to} ISO strings (UTC) or nulls for "all".
export const DATE_RANGE_OPTIONS = [
  { value: "", label: "All time" },
  { value: "today", label: "Today" },
  { value: "this_week", label: "This week" },
  { value: "this_month", label: "This month" },
  { value: "this_quarter", label: "This quarter" },
  { value: "last_30", label: "Last 30 days" },
];

function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }

export function computeDateRange(preset) {
  if (!preset) return { from: null, to: null };
  const now = new Date();
  const to = new Date(now); to.setHours(23, 59, 59, 999); // stable end-of-day so paged requests share a cache key
  let from = null;
  if (preset === "today") {
    from = startOfDay(now);
  } else if (preset === "this_week") {
    const d = startOfDay(now);
    const dow = (d.getDay() + 6) % 7; // Monday = 0
    d.setDate(d.getDate() - dow);
    from = d;
  } else if (preset === "this_month") {
    from = new Date(now.getFullYear(), now.getMonth(), 1);
  } else if (preset === "this_quarter") {
    const q = Math.floor(now.getMonth() / 3);
    from = new Date(now.getFullYear(), q * 3, 1);
  } else if (preset === "last_30") {
    from = startOfDay(new Date(now.getTime() - 30 * 86400000));
  }
  return { from: from ? from.toISOString() : null, to: to.toISOString() };
}

export const dateRangeLabel = (preset) =>
  DATE_RANGE_OPTIONS.find((o) => o.value === preset)?.label || "All time";

// Unified date filter value: { preset, from, to, field } — `from`/`to` are
// YYYY-MM-DD strings used when preset === "custom".
export const EMPTY_DATE_FILTER = { preset: "", from: "", to: "", field: "" };

export function normalizeDateFilter(v) {
  if (!v) return { ...EMPTY_DATE_FILTER };
  if (typeof v === "string") return { ...EMPTY_DATE_FILTER, preset: v };
  return { ...EMPTY_DATE_FILTER, ...v };
}

export function resolveDateFilter(df) {
  const d = normalizeDateFilter(df);
  if (d.preset !== "custom") return computeDateRange(d.preset);
  return {
    from: d.from ? new Date(`${d.from}T00:00:00`).toISOString() : null,
    to: d.to ? new Date(`${d.to}T23:59:59.999`).toISOString() : null,
  };
}

export const isDateFilterActive = (df) => {
  const d = normalizeDateFilter(df);
  return d.preset === "custom" ? !!(d.from || d.to) : !!d.preset;
};

export function dateFilterLabel(df) {
  const d = normalizeDateFilter(df);
  if (d.preset !== "custom") return dateRangeLabel(d.preset);
  const f = (s) => (s ? new Date(`${s}T00:00:00`).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "…");
  return `${f(d.from)} – ${f(d.to)}`;
}

export function applyDateFilterParams(params, df) {
  if (!isDateFilterActive(df)) return params;
  const { from, to } = resolveDateFilter(df);
  if (from) params.set("date_from", from);
  if (to) params.set("date_to", to);
  const field = normalizeDateFilter(df).field;
  if (field) params.set("date_field", field);
  return params;
}
